import { type FunctionComponent, memo, useCallback } from 'react';

import FeedbackIcon from '@atlaskit/icon/glyph/feedback';
import { Analytics } from '@trello/atlassian-analytics';
import {
  MirrorCardsFeedbackKey,
  PersonalProductivityEntryPointId,
  useFeedbackCollector,
} from '@trello/feedback-collector';
import { intl } from '@trello/i18n';
import { useBoardId, useCardId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';

import styles from './MirrorCardFeedback.less';

export const MirrorCardFeedback: FunctionComponent = memo(() => {
  const { FeedbackCollector, showFeedbackCollector } = useFeedbackCollector({
    entrypointId: PersonalProductivityEntryPointId,
    source: 'mirrorCard',
    attributes: { 'project-key': MirrorCardsFeedbackKey },
    feedbackCollectorProps: {
      feedbackTitle: intl.formatMessage({
        id: 'templates.mirror_card.feedback.title',
        defaultMessage: 'Share your thoughts on Mirror Cards',
        description: 'Feedback collector title for Mirror Cards',
      }),
    },
    getFeedbackContextOverride: () => `project-key: ${MirrorCardsFeedbackKey}`,
  });

  const idBoard = useBoardId();
  const idCard = useCardId();

  const onFeedbackButtonClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'mirrorCardFeedbackButton',
      source: 'mirrorCard',
      attributes: {
        containerType: 'MirrorCard',
      },
      containers: {
        board: {
          id: idBoard,
        },
        card: {
          id: idCard,
        },
      },
    });
    showFeedbackCollector();
  }, [idBoard, idCard, showFeedbackCollector]);

  return (
    <>
      <FeedbackCollector />
      <Tooltip
        content={intl.formatMessage({
          id: 'templates.mirror_card.feedback.tooltip',
          defaultMessage: 'Give feedback on Mirror Cards',
          description: 'Feedback tooltip for Mirror Cards',
        })}
      >
        <Button
          onClick={onFeedbackButtonClick}
          className={styles.mirrorCardFeedback}
          tabIndex={-1}
        >
          <FeedbackIcon
            size="small"
            label={intl.formatMessage({
              id: 'templates.mirror_card.feedback.button-label',
              defaultMessage: 'Mirror Card Feedback',
              description: 'Feedback button label for Mirror Cards',
            })}
          />
        </Button>
      </Tooltip>
    </>
  );
});
